import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Users, 
  Check, 
  Trophy, 
  Clock, 
  MousePointer2,
  Loader2,
  Star,
  Swords,
  RotateCcw,
  Shuffle,
  Calendar,
  X,
  ArrowLeft
} from 'lucide-react';
import { formatTime } from '../../lib/gameUtils';
import { DIFFICULTIES } from '../../lib/constants';
import { cn, isMobileDevice } from '../../lib/utils';
import { logger } from '../../lib/utils';
import axios from 'axios';
import { API, getChallengeUrl, handleCopyToClipboard } from './challengeUtils';
import { toast } from 'sonner';
import { isProfane, sanitizeName } from '../../lib/profanityFilter';

// Format date for display (e.g., "Dec 28" or "Dec 28, 2025" if different year)
const formatLeaderboardDate = (isoDate) => {
  if (!isoDate) return null;
  const date = new Date(isoDate);
  const now = new Date();
  const isCurrentYear = date.getFullYear() === now.getFullYear();
  
  const options = { month: 'short', day: 'numeric' };
  if (!isCurrentYear) {
    options.year = 'numeric';
  }
  return date.toLocaleDateString('en-US', options);
};

export function ChallengeCompletionModal({ 
  isOpen, 
  onClose, 
  scoreData,
  time,
  moves,
  challengeCode,
  onSaveScore,
  onNewPuzzle,
  onReplayPuzzle,
  deviceId
}) {
  const [step, setStep] = useState('result'); // 'result' | 'enterName' | 'comparison'
  const [playerName, setPlayerName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savedResult, setSavedResult] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [challengeInfo, setChallengeInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const loadChallengeInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/challenges/${challengeCode}?is_mobile=${isMobileDevice()}`);
      setChallengeInfo(response.data);
    } catch (err) {
      logger.error('Failed to load challenge info:', err);
    }
  }, [challengeCode]);
  
  const loadLeaderboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/challenges/${challengeCode}/leaderboard?is_mobile=${isMobileDevice()}`);
      setLeaderboard(response.data.leaderboard || []);
    } catch (err) {
      logger.error('Failed to load leaderboard:', err);
    }
  }, [challengeCode]);
  
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('result');
      setPlayerName(sanitizeName(localStorage.getItem('jigsaw_daily_player_name') || ''));
      setIsSubmitting(false);
      setSavedResult(null);
      setLeaderboard([]);
      // Load challenge info and existing leaderboard
      loadChallengeInfo();
      loadLeaderboard();
    }
  }, [isOpen, challengeCode, loadChallengeInfo, loadLeaderboard]);
  
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!playerName.trim() || isSubmitting) return;

    if (isProfane(playerName.trim())) {
      toast.error('Profanity Detected! - Try Again');
      return;
    }

    setIsSubmitting(true);
    try {
      const isMobile = isMobileDevice();
      const response = await axios.post(`${API}/challenges/${challengeCode}/score?is_mobile=${isMobile}`, {
        player_name: playerName.trim(),
        time_seconds: time,
        moves,
        device_id: deviceId
      });
      setSavedResult(response.data);
      
      // Also save to completed puzzles history (with grid_size)
      if (deviceId && challengeInfo?.image_url) {
        try {
          await axios.post(`${API}/images/completed`, {
            device_id: deviceId,
            image_url: challengeInfo.image_url,
            player_name: playerName.trim(),
            time_seconds: time,
            moves,
            score: scoreData?.score || 0,
            difficulty: challengeInfo.difficulty || 'normal',
            perfect_game: scoreData?.isPerfect || false,
            grid_size: isMobile ? 4 : 6
          });
        } catch (historyError) {
          logger.error('Failed to save to history:', historyError);
        }
      }
      
      // Save player name for future use
      localStorage.setItem('jigsaw_daily_player_name', playerName.trim());
      localStorage.setItem('jigsaw_player_name', playerName.trim());
      // Load leaderboard
      await loadLeaderboard();
      setStep('comparison');
      onSaveScore?.(response.data);
    } catch (error) {
      logger.error('Failed to submit score:', error);
      setIsSubmitting(false);
    }
  };
  
  // Handle save click - submit directly if name exists, otherwise go to enterName step
  const handleSaveClick = async () => {
    if (playerName.trim()) {
      await handleSubmit();
    } else {
      setStep('enterName');
    }
  };
  
  const handleCopyLink = async () => {
    const url = getChallengeUrl(challengeCode);
    handleCopyToClipboard(
      url,
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      null
    );
  };
  
  if (!scoreData) return null;
  
  const { score, isPerfect } = scoreData;
  const diffConfig = challengeInfo ? DIFFICULTIES[challengeInfo.difficulty] : null;
  
  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-lg w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] sm:w-auto p-0 overflow-y-auto max-h-[90vh] sm:max-h-[85vh] box-border" hideCloseButton data-testid="challenge-completion-modal">
        <DialogTitle className="sr-only">Challenge Complete</DialogTitle>
        {step === 'result' ? (
          // Step 1: Show result
          <div className="p-6 space-y-4 relative">
            {/* Close button */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onNewPuzzle}
              className="absolute top-2 right-2 h-8 w-8 z-10"
              data-testid="challenge-completion-close-btn"
            >
              <X className="h-4 w-4" />
            </Button>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                <Swords className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-2xl font-heading font-bold">Challenge Complete!</h2>
              <p className="text-muted-foreground mt-1">
                {isPerfect ? "Perfect game!" : "Great job!"}
              </p>
            </div>
            
            {/* Score */}
            <div className="text-center py-3">
              <div className="inline-flex items-center gap-3 px-6 py-3 rounded-2xl bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20">
                <Trophy className="h-7 w-7 text-primary" />
                <div>
                  <div className="text-3xl font-heading font-bold text-gradient">
                    {Math.round(score)}
                  </div>
                  <div className="text-xs text-muted-foreground">Points</div>
                </div>
              </div>
            </div>
            
            {/* Stats */}
            <div className="flex justify-center gap-6 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span className="font-semibold text-foreground">{formatTime(time)}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <MousePointer2 className="h-4 w-4" />
                <span className="font-semibold text-foreground">{moves} moves</span>
              </div>
            </div>
            
            {isPerfect && (
              <div className="flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-warning/10 border border-warning/30">
                <Star className="h-4 w-4 text-warning fill-warning" />
                <span className="text-sm font-medium text-warning">Perfect Game Bonus!</span>
              </div>
            )}
            
            {/* Name Input for Save Score */}
            <Input
              type="text"
              placeholder="Enter your name"
              value={playerName}
              onChange={(e) => setPlayerName(sanitizeName(e.target.value))}
              className="text-center h-11"
              maxLength={20}
              data-testid="challenge-player-name-input"
            />
            
            <Button 
              onClick={handleSaveClick}
              disabled={isSubmitting}
              className="w-full h-11 bg-gradient-to-r from-primary to-accent hover:opacity-90"
              data-testid="save-challenge-score-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Score to Leaderboard'
              )}
            </Button>
            
            <Button 
              variant="ghost" 
              onClick={onNewPuzzle}
              className="w-full"
            >
              Skip
            </Button>
          </div>
        ) : step === 'enterName' ? (
          // Step 2: Enter name
          <div className="p-6 space-y-4 relative">
            {/* Back button */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setStep('result')}
              className="absolute top-2 left-2 h-8 w-8 z-10"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            {/* Close button */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onNewPuzzle}
              className="absolute top-2 right-2 h-8 w-8 z-10"
            >
              <X className="h-4 w-4" />
            </Button>
            <div className="text-center pt-6">
              <h2 className="text-xl font-heading font-bold">Enter Your Name</h2>
              <p className="text-muted-foreground text-sm mt-1">
                Save your score to the challenge leaderboard
              </p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                placeholder="Enter your name..."
                value={playerName}
                onChange={(e) => setPlayerName(sanitizeName(e.target.value))}
                maxLength={20}
                className="h-11 text-center"
                autoFocus
                data-testid="challenge-player-name-input"
              />
              
              <div className="flex gap-3">
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => setStep('result')}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  type="submit"
                  disabled={!playerName.trim() || isSubmitting}
                  className="flex-1 bg-gradient-to-r from-primary to-accent hover:opacity-90"
                  data-testid="submit-challenge-score-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Score'
                  )}
                </Button>
              </div>
            </form>
          </div>
        ) : (
          // Step 3: Score Comparison & Leaderboard
          <div className="p-6 space-y-4 relative">
            {/* Close button */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onNewPuzzle}
              className="absolute top-2 right-2 h-8 w-8 z-10"
              data-testid="challenge-comparison-close-btn"
            >
              <X className="h-4 w-4" />
            </Button>
            {/* Header with result */}
            <div className="text-center">
              {savedResult && leaderboard.length > 0 && (() => {
                // Find the current player's entry (the one just submitted)
                const playerEntry = leaderboard.find(e => e.player_name === playerName);
                // Find the best competitor score (anyone other than current player)
                const competitorEntries = leaderboard.filter(e => e.player_name !== playerName);
                const bestCompetitor = competitorEntries.length > 0 ? competitorEntries[0] : null;
                
                // Determine win/loss/tie based on current player vs best competitor
                const playerScore = playerEntry ? Math.round(playerEntry.score) : Math.round(score);
                const competitorScore = bestCompetitor ? Math.round(bestCompetitor.score) : 0;
                
                const playerWon = bestCompetitor ? playerScore > competitorScore : true;
                const isTie = bestCompetitor && playerScore === competitorScore;
                const isOnlyPlayer = !bestCompetitor;
                
                return (
                  <>
                    <div className={cn(
                      "inline-flex items-center justify-center w-14 h-14 rounded-full mb-3",
                      isOnlyPlayer ? "bg-primary/10" : playerWon ? "bg-success/10" : isTie ? "bg-warning/10" : "bg-muted"
                    )}>
                      {isOnlyPlayer ? (
                        <Trophy className="h-7 w-7 text-primary" />
                      ) : playerWon ? (
                        <Trophy className="h-7 w-7 text-success" />
                      ) : isTie ? (
                        <Users className="h-7 w-7 text-warning" />
                      ) : (
                        <Swords className="h-7 w-7 text-muted-foreground" />
                      )}
                    </div>
                    <h2 className="text-xl font-heading font-bold">
                      {isOnlyPlayer ? "First to Complete!" : playerWon ? "You Won!" : isTie ? "It's a Tie!" : "Challenge Accepted!"}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Ranked <span className="font-bold text-primary">#{savedResult.rank}</span> of {savedResult.total_participants}
                    </p>
                  </>
                );
              })()}
              {savedResult && leaderboard.length === 0 && (
                <>
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-3">
                    <Trophy className="h-7 w-7 text-primary" />
                  </div>
                  <h2 className="text-xl font-heading font-bold">Score Saved!</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Ranked <span className="font-bold text-primary">#{savedResult.rank}</span> of {savedResult.total_participants}
                  </p>
                </>
              )}
            </div>
            
            {/* Score Comparison Cards */}
            {leaderboard.length > 0 && (() => {
              // Find the current player's entry
              const playerEntry = leaderboard.find(e => e.player_name === playerName);
              // Find the best competitor (anyone other than current player)
              const competitorEntries = leaderboard.filter(e => e.player_name !== playerName);
              const bestCompetitor = competitorEntries.length > 0 ? competitorEntries[0] : null;
              
              if (playerEntry && bestCompetitor) {
                const playerWon = Math.round(playerEntry.score) > Math.round(bestCompetitor.score);
                const scoreDiff = Math.abs(Math.round(playerEntry.score) - Math.round(bestCompetitor.score));
                
                return (
                  <div className="grid grid-cols-2 gap-3" data-testid="score-comparison">
                    {/* Your Score */}
                    <div className={cn(
                      "p-4 rounded-xl border-2 text-center",
                      playerWon ? "bg-success/5 border-success/30" : "bg-muted/50 border-transparent"
                    )}>
                      <p className="text-xs text-muted-foreground mb-1">You</p>
                      <p className="font-semibold truncate">{playerEntry.player_name}</p>
                      <div className={cn(
                        "text-3xl font-heading font-bold mt-2",
                        playerWon ? "text-success" : "text-foreground"
                      )}>
                        {Math.round(playerEntry.score)}
                      </div>
                      <div className="flex items-center justify-center gap-2 mt-2 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        <span>{formatTime(playerEntry.time_seconds)}</span>
                        <span>·</span>
                        <span>{playerEntry.moves}m</span>
                      </div>
                      {playerWon && (
                        <div className="mt-2 text-xs font-medium text-success">
                          +{scoreDiff} pts
                        </div>
                      )}
                    </div>
                    
                    {/* Best Competitor Score */}
                    <div className={cn(
                      "p-4 rounded-xl border-2 text-center",
                      !playerWon ? "bg-destructive/5 border-destructive/30" : "bg-muted/50 border-transparent"
                    )}>
                      <p className="text-xs text-muted-foreground mb-1">
                        {bestCompetitor.player_name === challengeInfo?.creator_name ? "Challenger" : "Top Score"}
                      </p>
                      <p className="font-semibold truncate">{bestCompetitor.player_name}</p>
                      <div className={cn(
                        "text-3xl font-heading font-bold mt-2",
                        !playerWon ? "text-destructive" : "text-foreground"
                      )}>
                        {Math.round(bestCompetitor.score)}
                      </div>
                      <div className="flex items-center justify-center gap-2 mt-2 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        <span>{formatTime(bestCompetitor.time_seconds)}</span>
                        <span>·</span>
                        <span>{bestCompetitor.moves}m</span>
                      </div>
                      {!playerWon && scoreDiff > 0 && (
                        <div className="mt-2 text-xs font-medium text-destructive">
                          +{scoreDiff} pts
                        </div>
                      )}
                    </div>
                  </div>
                );
              }
              return null;
            })()}
            
            {/* Full Leaderboard */}
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-1">
                Challenge Leaderboard
              </p>
              <div className="max-h-40 overflow-y-auto space-y-1.5 pr-1">
                {leaderboard.map((entry, index) => (
                  <div 
                    key={entry.id}
                    className={cn(
                      "flex items-center gap-2 p-2.5 rounded-lg text-sm",
                      entry.player_name === playerName 
                        ? "bg-primary/10 border border-primary/30" 
                        : entry.player_name === challengeInfo?.creator_name
                          ? "bg-accent/10 border border-accent/30"
                          : "bg-muted/30"
                    )}
                  >
                    <span className={cn(
                      "w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-bold",
                      index === 0 && "bg-yellow-500 text-white",
                      index === 1 && "bg-gray-400 text-white",
                      index === 2 && "bg-amber-600 text-white",
                      index > 2 && "bg-muted text-muted-foreground"
                    )}>
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium truncate">{entry.player_name}</span>
                        {entry.player_name === playerName && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/20 text-primary font-medium">YOU</span>
                        )}
                        {entry.player_name === challengeInfo?.creator_name && entry.player_name !== playerName && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent/20 text-accent-foreground font-medium">CHALLENGER</span>
                        )}
                      </div>
                      {entry.created_at && (
                        <div className="flex items-center gap-1 text-[10px] text-muted-foreground mt-0.5">
                          <Calendar className="h-2.5 w-2.5" />
                          {formatLeaderboardDate(entry.created_at)}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="font-bold">{Math.round(entry.score)}</span>
                      {entry.perfect_game && <Star className="h-3 w-3 text-warning fill-warning" />}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* New Puzzle Button */}
            <div className="pt-2 flex gap-3">
              <Button 
                onClick={onNewPuzzle}
                className="flex-1 bg-gradient-to-r from-primary to-accent hover:opacity-90"
                data-testid="challenge-new-puzzle-btn"
              >
                <Shuffle className="h-4 w-4 mr-2" />
                New Puzzle
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
